// pms-adapters / supabase / departures — « Départs de demain », interim adapter
// (PMS-TERMINAL-LINK-BRIEF §8.8).
//
// The Vega side of the DepartureBriefing port: one RPC,
// `pms_departures_for_date`, which reads the mirror (vega_stay + the FACT
// AUTO plan vega_folio_line + the running bill vega_bill_line + the arrhes
// register vega_open_deposit + the notes register vega_open_note, whose
// pending notes are the bill once the guest has left — §8.8h) and the
// Encaissement log internally and returns one row per departing booking. At
// go-live a PMS adapter over pms_room_stay and pms_folio_position replaces this
// one; the sheet does not change.
//
// Read-only, so no L2 gate: the RPC gates itself on `pms.payment.read`, and a
// refusal comes back as an ordinary error.

import type {
  DebtorAccount,
  DepartureBill,
  DepartureBriefing,
  DismissedUnissued,
  Instant,
  IsoDate,
  OpenSuspense,
  PaymentLine,
  PendingNote,
  ReceptionPaymentId,
  Result,
  TransferredNote,
  UnbilledLine,
  UnissuedBill,
} from '@lemontaigu/pms-core';
import { fromList, fromResult, type SupabaseClient } from './support.js';

export interface DepartureRow {
  property_code: string;
  resa: string;
  /** The billing account (20260925090100) — null for a single-account booking. */
  unit?: string | null;
  unit_label?: string | null;
  is_main?: boolean | null;
  last_paid_at?: string | null;
  party_label: string | null;
  rooms: string[] | null;
  bill_cents: number;
  folio_lines: number;
  collected_cents: number;
  pending_cents: number;
  deposit_cents: number;
  deposit_unattributed_cents: number;
  unreleased: boolean | null;
  /** « Vega relu à » (20260925070100) — was `folio_read_at`, the last change. */
  read_at: string | null;
  payments: PaymentRow[] | null;
  checked_out: boolean | null;
  pending_notes: PendingNoteRow[] | null;
  unbilled_lines: UnbilledLineRow[] | null;
  /** The account's open « en attente » (20260926010100) — optional so a row of
   *  the previous shape still reads. */
  suspenses?: SuspenseRow[] | null;
  /** Notes put on a debtor account (20260926020100) — optional likewise. */
  debts?: TransferredNoteRow[] | null;
  debt_cents?: number | null;
  /** The booking's Vega client number (20260926030100). */
  client_ref?: string | null;
}

export interface TransferredNoteRow {
  facture: string;
  date: string | null;
  at: string | null;
  cents: number;
  recovered: number | null;
  debtor: string | null;
  account: string | null;
}

export const toTransferredNote = (d: TransferredNoteRow): TransferredNote => ({
  facture: d.facture,
  date: d.date as IsoDate | null,
  at: d.at as Instant | null,
  cents: Number(d.cents),
  recoveredCents: Number(d.recovered ?? 0),
  debtor: d.debtor,
  account: d.account,
});

/** One row of `pms_debtors` (20260926020100). */
export interface DebtorRow {
  property_code: string;
  client_ref: string;
  debtor_name: string | null;
  balance_cents: number;
  opened_on: string | null;
  movements: DebtorMovementRow[] | null;
  read_at: string | null;
}

interface DebtorMovementRow {
  kind: 'transfer' | 'recovery';
  facture: string | null;
  date: string | null;
  at: string | null;
  method: string | null;
  label: string | null;
  cents: number;
  resa: string | null;
  party: string | null;
  account: string | null;
}

export const toDebtorAccount = (r: DebtorRow): DebtorAccount => ({
  propertyCode: r.property_code,
  clientRef: r.client_ref,
  name: r.debtor_name ?? r.client_ref,
  balanceCents: Number(r.balance_cents),
  openedOn: r.opened_on as IsoDate | null,
  movements: (r.movements ?? []).map((m) => ({
    kind: m.kind,
    facture: m.facture,
    date: m.date as IsoDate | null,
    at: m.at as Instant | null,
    label: m.label,
    cents: Number(m.cents),
    bookingRef: m.resa,
    partyLabel: m.party,
    account: m.account,
  })),
  readAt: (r.read_at ?? null) as Instant | null,
});

export interface SuspenseRow {
  id: number;
  cents: number;
  at: string;
  room: string | null;
}

export const toOpenSuspense = (s: SuspenseRow): OpenSuspense => ({
  id: Number(s.id) as ReceptionPaymentId,
  cents: Number(s.cents),
  at: s.at as Instant,
  roomNumber: s.room,
});

interface PendingNoteRow {
  facture: string;
  date: string | null;
  cents: number;
}

interface UnbilledLineRow {
  label: string | null;
  date: string | null;
  qty: number | null;
  unitPriceCents: number | null;
  cents: number;
}

const toPendingNote = (n: PendingNoteRow): PendingNote => ({
  facture: n.facture,
  date: n.date as IsoDate | null,
  cents: Number(n.cents),
});

const toUnbilledLine = (l: UnbilledLineRow): UnbilledLine => ({
  label: l.label,
  date: l.date as IsoDate | null,
  qty: l.qty == null ? null : Number(l.qty),
  unitPriceCents: l.unitPriceCents == null ? null : Number(l.unitPriceCents),
  cents: Number(l.cents),
});

/** One entry of the `payments` jsonb both sheets return (pms_vega_booking_money). */
export interface PaymentRow {
  date: string | null;
  /** When it was keyed (20260925090100). */
  at?: string | null;
  source: PaymentLine['source'];
  method: string | null;
  label: string | null;
  card: boolean | null;
  cents: number;
}

export const toPaymentLine = (p: PaymentRow): PaymentLine => ({
  date: p.date as IsoDate | null,
  at: (p.at ?? null) as Instant | null,
  source: p.source,
  method: p.method,
  label: p.label,
  isCard: p.card,
  cents: Number(p.cents),
});

export const toDepartureBill = (r: DepartureRow): DepartureBill => ({
  propertyCode: r.property_code,
  bookingRef: r.resa,
  partyLabel: r.party_label ?? '—',
  rooms: r.rooms ?? [],
  billCents: Number(r.bill_cents),
  folioLineCount: r.folio_lines,
  collectedCents: Number(r.collected_cents),
  pendingCents: Number(r.pending_cents),
  depositCents: Number(r.deposit_cents ?? 0),
  depositUnattributedCents: Number(r.deposit_unattributed_cents ?? 0),
  unreleased: r.unreleased === true,
  readAt: (r.read_at ?? null) as Instant | null,
  payments: (r.payments ?? []).map(toPaymentLine),
  checkedOut: r.checked_out === true,
  pendingNotes: (r.pending_notes ?? []).map(toPendingNote),
  unbilledLines: (r.unbilled_lines ?? []).map(toUnbilledLine),
  unit: r.unit ?? null,
  unitLabel: r.unit_label ?? null,
  isMain: r.is_main !== false,
  lastPaidAt: (r.last_paid_at ?? null) as Instant | null,
  suspenses: (r.suspenses ?? []).map(toOpenSuspense),
  debts: (r.debts ?? []).map(toTransferredNote),
  debtCents: Number(r.debt_cents ?? 0),
  clientRef: r.client_ref ?? null,
});

/** One row of `pms_unissued_bills` (20260925080100). */
export interface UnissuedRow {
  property_code: string;
  resa: string;
  party_label: string | null;
  rooms: string[] | null;
  arrival_date: string | null;
  departure_date: string | null;
  vega_status: string | null;
  line_count: number;
  first_day: string | null;
  last_day: string | null;
  amount_cents: number;
  deposit_cents: number;
  deposit_unattributed_cents: number;
  payments: PaymentRow[] | null;
  read_at: string | null;
  client_ref?: string | null;
}

export const toUnissuedBill = (r: UnissuedRow): UnissuedBill => ({
  propertyCode: r.property_code,
  bookingRef: r.resa,
  partyLabel: r.party_label ?? '—',
  rooms: r.rooms ?? [],
  arrivalDate: r.arrival_date as IsoDate | null,
  departureDate: r.departure_date as IsoDate | null,
  vegaStatus: r.vega_status,
  lineCount: Number(r.line_count),
  firstDay: r.first_day as IsoDate | null,
  lastDay: r.last_day as IsoDate | null,
  amountCents: Number(r.amount_cents),
  depositCents: Number(r.deposit_cents ?? 0),
  depositUnattributedCents: Number(r.deposit_unattributed_cents ?? 0),
  payments: (r.payments ?? []).map(toPaymentLine),
  readAt: (r.read_at ?? null) as Instant | null,
  clientRef: r.client_ref ?? null,
});

/** One row of `pms_unissued_dismissed` (20260930030100). */
export interface DismissedUnissuedRow {
  property_code: string;
  resa: string;
  party_label: string | null;
  line_count: number;
  amount_cents: number;
  first_day: string | null;
  last_day: string | null;
  reason: string;
  dismissed_at: string;
}

export const toDismissedUnissued = (r: DismissedUnissuedRow): DismissedUnissued => ({
  propertyCode: r.property_code,
  bookingRef: r.resa,
  partyLabel: r.party_label ?? '—',
  lineCount: Number(r.line_count),
  amountCents: Number(r.amount_cents),
  firstDay: r.first_day as IsoDate | null,
  lastDay: r.last_day as IsoDate | null,
  reason: r.reason,
  dismissedAt: r.dismissed_at as Instant,
});

export class SupabaseVegaDepartureBriefing implements DepartureBriefing {
  constructor(private readonly db: SupabaseClient) {}

  async departures(date: IsoDate): Promise<Result<DepartureBill[]>> {
    const r = await this.db.rpc('pms_departures_for_date', { p_date: date });
    return fromList<DepartureRow, DepartureBill>(
      r as { data: DepartureRow[] | null; error: typeof r.error },
      toDepartureBill,
    );
  }

  /** « Non facturés » — stays over whose bill was posted and never invoiced. */
  async unissued(): Promise<Result<UnissuedBill[]>> {
    const r = await this.db.rpc('pms_unissued_bills');
    return fromList<UnissuedRow, UnissuedBill>(
      r as { data: UnissuedRow[] | null; error: typeof r.error },
      toUnissuedBill,
    );
  }

  /** Sets a booking's listed lines aside (20260930030100). The RPC refuses a
   *  booking the list does not show and a reason under 10 characters. */
  async dismissUnissued(propertyCode: string, bookingRef: string, reason: string): Promise<Result<number>> {
    const r = await this.db.rpc('pms_unissued_dismiss', {
      p_property_code: propertyCode,
      p_resa: bookingRef,
      p_reason: reason,
    });
    return fromResult<number>(r as { data: number | null; error: typeof r.error });
  }

  async restoreUnissued(propertyCode: string, bookingRef: string): Promise<Result<number>> {
    const r = await this.db.rpc('pms_unissued_restore', { p_property_code: propertyCode, p_resa: bookingRef });
    return fromResult<number>(r as { data: number | null; error: typeof r.error });
  }

  async dismissedUnissued(): Promise<Result<DismissedUnissued[]>> {
    const r = await this.db.rpc('pms_unissued_dismissed');
    return fromList<DismissedUnissuedRow, DismissedUnissued>(
      r as { data: DismissedUnissuedRow[] | null; error: typeof r.error },
      toDismissedUnissued,
    );
  }

  /** « Clients débiteurs » — debtor accounts with a balance (§8.8l). */
  async debtors(): Promise<Result<DebtorAccount[]>> {
    const r = await this.db.rpc('pms_debtors');
    return fromList<DebtorRow, DebtorAccount>(
      r as { data: DebtorRow[] | null; error: typeof r.error },
      toDebtorAccount,
    );
  }
}
